import React, { useMemo, useCallback, useEffect, useState } from 'react'
import { Field as FormikField, connect } from 'formik'
import { Field } from '@/ui'
import styles from './ContractPosition.module.scss'
import { useTranslation } from 'react-i18next'
import { getCurrentLanguage } from '@/i18next'
import { usePrevious } from '@/shared'
import { useDispatch, useSelector } from 'react-redux'
import { Radio } from '@/entity/contracts/ui/form/components'
import { FormControlLabel, RadioGroup } from '@mui/material'
import cl from 'classnames'
import { getPositionsByEstablishedPost } from '@/entity/staffingProject/thunks'
import { employerProfileSelectors } from '@features/employerProfile/model'
import { handbookSelectors } from '@/entity/handbook'

const INPUT_TYPE = {
	manual: 'manual',
	directory: 'directory',
}

export const ContractPosition = connect(
	({
		 positionsByKnz = [],
		 positionsByKnzFull = [],
		 establishedPostPositionCategoryRelation = [],
		 formik: { setFieldValue, values, errors },
		 isFormDirty,
		 isElectronicButton = true,
		 immigrant = false,
		 isEdit,
	 }) => {
		const { t } = useTranslation()
		const dispatch = useDispatch()
		const lang = getCurrentLanguage()
		const { age } = useSelector((state) => state.contracts.contract.data)
		const activeCompany = useSelector(employerProfileSelectors.activeCompany)
		const isTeenager = Number.isFinite(age) && age < 18
		const { lmc, sphereType } = activeCompany

		// Получение справочников через handbookSelectors
		const positions = useSelector(handbookSelectors.positions)
		const establishedPostBySchool = useSelector(handbookSelectors.establishedPostBySchool)
		const establishedPostBySport = useSelector(handbookSelectors.establishedPostBySport)
		const establishedPostByCulture = useSelector(handbookSelectors.establishedPostByCulture)
		const establishedPostByHealth = useSelector(handbookSelectors.establishedPostByHealth)

		const workCondition = useSelector(handbookSelectors.workCondition)

		const { dpositionCode, dworkingConditionsCode } = values

		const prevValue = usePrevious(dworkingConditionsCode)

		const dicConstantWorkingConditionsCode = useMemo(() => {
			return immigrant || isTeenager ? '0' : values.dworkingConditionsCode
		}, [immigrant, isTeenager, values.dworkingConditionsCode])

		// Вычисление отфильтрованных позиций на основе sphereType
		const filteredPositions = useMemo(() => {
			if (!sphereType) {
				return positions?.filter((item) => item.isLast && (!lmc || item.lmc)) || []
			}

			const sphereTypeMap = {
				school: establishedPostBySchool,
				sport: establishedPostBySport,
				culture: establishedPostByCulture,
				healthcare: establishedPostByHealth,
			}

			return sphereTypeMap[sphereType] || positions?.filter((item) => item.isLast && (!lmc || item.lmc)) || []
		}, [sphereType, positions, lmc, establishedPostBySchool, establishedPostBySport, establishedPostByCulture, establishedPostByHealth])

		const [filteredPositionsByKnzByLmc, setFilteredPositionsByKnzByLmc] = useState(positionsByKnz)
		const [isLmcDisabled, setIsLmcDisabled] = useState(true)
		const [inputType, setInputType] = useState(INPUT_TYPE.directory)

		const sphereOptions = filteredPositions?.filter((item) => item.isLast) || []
		const lmcOptions = filteredPositions?.filter(i => i?.lmc) || []

		useEffect(() => {
			setFieldValue('dworkingConditionsCode', dicConstantWorkingConditionsCode)
		}, [dicConstantWorkingConditionsCode])

		useEffect(() => {
			if (!lmc && !sphereType) {
				setIsLmcDisabled(false)
			}
		}, [])

		useEffect(() => {
			if (lmc || sphereType) {
				if (filteredPositionsByKnzByLmc.length === 1) {
					setFieldValue('dpositionCode', filteredPositionsByKnzByLmc[0]?.code)
				}
			}
		}, [filteredPositionsByKnzByLmc, setFieldValue])

		useEffect(() => {
			if (!!dpositionCode) {
				GetPositionTree(dpositionCode)
			} else setFieldValue('positionParents', '')
		}, [dpositionCode])

		useEffect(() => {
			if (prevValue) {
				setFieldValue('positionParents', '')
				setFieldValue('dpositionCode', null)
				setFieldValue('dharmfulConditionsTypeCode', null)
			}
		}, [dworkingConditionsCode])

		useEffect(() => {
			if (immigrant || isTeenager) {
				setFieldValue('dworkingConditionsCode', '0')
			}
		}, [immigrant, isTeenager])


		let GetPositionTree = (code) => {
			const itemMap = {}
			positionsByKnzFull.forEach((item) => {
				itemMap[item.id] = item
			})
			const parentNames = []
			let currentItem = positionsByKnz.find((i) => i.code === code)

			while (currentItem && currentItem.parentId !== null) {
				parentNames.unshift(lang === 'rus' ? currentItem.rus : currentItem.kz)
				currentItem = itemMap[currentItem.parentId]
			}
			setFieldValue('positionParents', parentNames.join(' / '))
		}

		const establishedPosition = useMemo(
			() => values.destablishedPostCode || values.establishedPost,
			[values.destablishedPostCode, values.establishedPost]
		)

		const handleEstablishedPositionChange = useCallback(
			(code) => {
				setIsLmcDisabled(false)
				let position = filteredPositions?.find((item) => item.code === code)
				if (filteredPositions?.find((item) => item.code === code)) {
					/** Если значение из справочника, то заполняем destablishedPostCode */
					setFieldValue('establishedPost', lang === 'rus' ? position[lang || 'rus'] : position[lang || 'kz'])
					setFieldValue('destablishedPostCode', code)
				} else {
					/** Если ручной ввод, то заполняем значение establishedPost */
					setFieldValue('establishedPost', code)
					setFieldValue('destablishedPostCode', null)
				}
				if (lmc || sphereType) {
					setFieldValue('dpositionCode', null)

					if (!code) {
						setFieldValue('destablishedPostCode', null)
						return
					}
					setFieldValue('destablishedPostCode', code)
					const eppcr = establishedPostPositionCategoryRelation.filter((rel) => rel.establishedPostCode === code)

					if(lmc){
						let filtered = positionsByKnz.filter((item) => eppcr.some((x) => x.positionCode === item.code))
						setFilteredPositionsByKnzByLmc(filtered)
					}

					if (sphereType) {
						let body = {
							data: code,
							onSuccess: (res) => {
								let filtered = positionsByKnz.filter(i => {
									let find = res.find(r => r == i.code)
									return find
								})

								setFilteredPositionsByKnzByLmc(filtered)
							},
						}
						dispatch(getPositionsByEstablishedPost(body))
					}
				}
			},
			[filteredPositions, setFieldValue, lmc, sphereType, positionsByKnz, establishedPostPositionCategoryRelation, dispatch, lang]
		)

		// Здесь добавляем useMemo для фильтрации positionsByKnz
		const filteredPositionsByKnz = useMemo(() => {
			return immigrant ? positionsByKnz.filter((item) => item.laborImmigrant === true) : positionsByKnz
		}, [immigrant, positionsByKnz])

		const isAfter2014 = (dateString) => {
			if (!dateString) return false
			// Разделение строки даты на день, месяц и год
			const [day, month, year] = dateString.split('.')
			// Создание объекта Date с правильным форматом (месяцы в JS начинаются с 0)
			const dateFrom = new Date(year, month - 1, day)
			const referenceDate = new Date('2014-01-01') // для начала 2014 года
			return dateFrom >= referenceDate
		}


		useEffect(() => {
			if (isTeenager || immigrant) {
				if (values.dateFrom && !isAfter2014(values.dateFrom)) {
					if (values.dworkingConditionsCode === '2') {
						setFieldValue('dworkingConditionsCode', null)
						setFieldValue('dpositionCodeList1', null)
						setFieldValue('dpositionCodeList2', null)
						setFieldValue('dpositionCodeList3', null)
					}
				}
			}
		}, [values.dateFrom, isTeenager, immigrant])

		const filteredWorkCondition = useMemo(() => {
			if (isTeenager || immigrant) {
				if (!values.dateFrom || isAfter2014(values.dateFrom)) {
					return workCondition
				} else {
					return workCondition.filter((it) => it.code !== '2')
				}
			}
			return workCondition
		}, [isTeenager, immigrant, values.dateFrom, workCondition])

		return isElectronicButton ? (
			<div className={styles.col}>
				<Field
					options={filteredPositions}
					type="text"
					fieldType="selectSearch"
					isRequired
					placeholder={t('position_placeholder')}
					onChange={handleEstablishedPositionChange}
					value={establishedPosition}
					hint={t('hint_position')}
					label={t('contract.info.position')}
					error={isFormDirty && !establishedPosition && { key: 'required' }}
				/>
				<FormikField name="dpositionCode">
					{({ field, meta: { touched, error } }) => (
						<Field
							fieldType="selectSearch"
							label={t('contract.info.nkz_position')}
							hint={t('hint_position_knz')}
							placeholder={t('choose_from_directory')}
							options={(lmc || sphereType) ? filteredPositionsByKnzByLmc : filteredPositionsByKnz}
							error={(touched || isFormDirty) && error}
							isRequired
							disabled={(isEdit && isLmcDisabled) || (!isEdit && (lmc || sphereType) && filteredPositionsByKnzByLmc.length === 1)}
							{...field}
						/>
					)}
				</FormikField>
			</div>
		) : (
			<div className={styles.col}>
				<div className={styles.fields}>
					<RadioGroup value={inputType} onChange={(e, value) => {
						setInputType(value)
						setFieldValue('establishedPost', null)
					}} classes={{
						root: cl('flex', 'flex-row')
					}}>
						<FormControlLabel
							control={<Radio />}
							label={t('workplace.manual_input')}
							value={INPUT_TYPE.manual}
							disabled={lmc || sphereType}
						/>
						<FormControlLabel
							control={<Radio />}
							label={t('workplace.directory')}
							value={INPUT_TYPE.directory}
							disabled={lmc || sphereType}
						/>
					</RadioGroup>
				</div>
				<div className={styles.fields}>
					{
						inputType === INPUT_TYPE.manual ? (
							<Field
								options={filteredPositions}
								type="text"
								fieldType='input'
								isRequired
								placeholder={t('position_placeholder_second')}
								onChange={handleEstablishedPositionChange}
								value={values.establishedPost}
								hint={t('hint_position')}
								label={t('contract.info.position')}
								error={isFormDirty && !establishedPosition && { key: 'required' }}
							/>
						) : (
							<Field
								options={sphereType ? sphereOptions : lmc ? lmcOptions : filteredPositions}
								type="text"
								fieldType='selectSearch'
								isRequired
								placeholder={t('validation.required_nkz')}
								onChange={handleEstablishedPositionChange}
								value={establishedPosition}
								hint={t('hint_position')}
								label={t('contract.info.position')}
								error={isFormDirty && !establishedPosition && { key: 'required' }}
							/>
						)
					}
					<FormikField name="dworkingConditionsCode">
						{({ field, meta: { touched, error } }) => (
							<Field
								fieldType="select"
								isRequired
								label={t('contract.info.working_condition')}
								placeholder={t('choose_from_directory')}
								options={filteredWorkCondition}
								error={(touched || isFormDirty) && error}
								type="text"
								hint={immigrant || isTeenager ? '' : t('hint_working_condition')}
								{...field}
								disabled={immigrant || isTeenager}
							/>
						)}
					</FormikField>
				</div>
				<FormikField name="dpositionCode">
					{({ field, meta: { touched, error } }) => (
						<Field
							fieldType="selectSearch"
							label={t('contract.info.nkz_position')}
							hint={t('hint_position_knz')}
							placeholder={t('choose_from_directory')}
							options={(lmc || sphereType) ? filteredPositionsByKnzByLmc : filteredPositionsByKnz}
							error={(touched || isFormDirty) && error}
							isRequired
							disabled={(isEdit && isLmcDisabled) || (!isEdit && (lmc || sphereType) && filteredPositionsByKnzByLmc.length === 1)}
							{...field}
						/>
					)}
				</FormikField>
				<FormikField name="positionParents">
					{({ field }) => (
						<Field
							labelClassName={styles.formLabel}
							type="text"
							fieldType="textarea"
							label={t('hierarchy_categories')}
							rows={5}
							disabled={true}
							{...field}
						/>
					)}
				</FormikField>
			</div>
		)
	}
)
